import React from 'react';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';

import { Form } from 'react-bootstrap';
import { Checkbox } from 'pretty-checkbox-react';
import EnergyTracerMultiLineChart from '../../components/EnergyTracerMultiLineChart';
import GraphActionContainer from '../../components/chartElements/GraphActionContainer';
import ChartDownload from '../../components/chartElements/Actions/ChartDownload';
import {
  ghg_units,
  ghg_conversions,
} from '../../helpers/GHGUnits';

import 'pretty-checkbox';

const row = {
  marginRight: '40px',
  paddingLeft: '1em',
  marginTop: '1em',
};

function UtilityBreakdown(props) {
  const [deferredProps, setDeferredProps] = useState({ ...props });
  useEffect(() => {
    if (props.open) setDeferredProps({ ...props });
  }, [props]);
  const {
    dashboardData,
    baselineData,
    baseline2Data,
    organization,
    activeIds,
    activeGHGLicense,
  } = deferredProps;

  const [availableUtilities, setAvailableUtilities] = useState([]);
  const [selectedUtilities, setSelectedUtilities] = useState([]);
  const [usageSelectedUtilities, setUsageSelectedUtilities] = useState([]);

  const [costData, setCostData] = useState([]);
  const [usageData, setUsageData] = useState([]);
  const [GHGData, setGHGData] = useState([]);
  const [convertedGHGData, setConvertedGHGData] = useState([]);


  const [chartDownloadHeaders, setChartDownloadHeaders] = useState([]);
  const [chartDownloadKeys, setChartDownloadKeys] = useState([]);
  const [usageChartDownloadHeaders, setUsageChartDownloadHeaders] = useState([]);
  const [usageChartDownloadKeys, setUsageChartDownloadKeys] = useState([]);

  const [activeGHGUnit, setActiveGHGUnit] = useState('lbs');
  const [GHGUnitFactor, setGHGUnitFactor] = useState(1);


  useEffect(() => {
    if (Object.keys(dashboardData).length) {
      let chart_utilities = dashboardData['available_utilities'].map((utility) => (utility.charAt(0).toUpperCase() +
        utility.substr(1).toLowerCase()));
      setAvailableUtilities(chart_utilities);
      setSelectedUtilities(chart_utilities);
    }
  }, [
    dashboardData
  ])

  useEffect(() => {
    // usage selected utilities (usage and ghg ignore water)
    let usage_selected_utilities = []
    selectedUtilities.forEach((utility) => {
      if (!(['usage', 'ghg'].includes('usage') && utility === 'Water')) {
        usage_selected_utilities.push(utility)
      }
    })
    setUsageSelectedUtilities(usage_selected_utilities);

    let headers = ['Date'];
    let keys = ['name'];
    selectedUtilities.forEach((key) => {
      headers.push(key);
      keys.push(key);
      if (Object.keys(baselineData).length > 0) {
        headers.push(key + ' Baseline');
        keys.push(key + ' Baseline');
      }
      if (Object.keys(baseline2Data).length > 0) {
        headers.push(key + ' Baseline 2');
        keys.push(key + ' Baseline 2');
      }
    });
    setChartDownloadHeaders(headers);
    setChartDownloadKeys(keys);

    let usage_headers = ['Date'];
    let usage_keys = ['name'];
    usage_selected_utilities.forEach((key) => {
      usage_headers.push(key);
      usage_keys.push(key);
    });
    setUsageChartDownloadHeaders(usage_headers);
    setUsageChartDownloadKeys(usage_keys);

  }, [selectedUtilities, baselineData, baseline2Data,])

  const changeCheck = (check) => {
    let new_checks = [...selectedUtilities];
    if (new_checks.includes(check)) {
      new_checks = new_checks.filter(function (item) {
        return item !== check;
      });
    } else {
      new_checks.push(check);
    }
    new_checks = new_checks.sort()
    setSelectedUtilities(new_checks);
  };



  useEffect(() => {
    setGHGUnitFactor(1 / ghg_conversions[activeGHGUnit]);
  }, [activeGHGUnit]);

  useEffect(() => {
    let altered_ghg_data = [];
    GHGData.forEach((month) => {
      let temp_month = {};
      Object.keys(month).forEach((key) => {
        if (['name', 'month'].includes(key)) {
          temp_month[key] = month[key];
        } else {
          temp_month[key] = month[key] * GHGUnitFactor;
        }
      });
      altered_ghg_data.push(temp_month);
    });
    setConvertedGHGData(altered_ghg_data);
  }, [GHGData, GHGUnitFactor]);




  const convertToChartForm = useCallback((dashboardData, baselineData, baseline2Data, selectedUtilities, chartType, activeIds) => {
    const hasBaseline = Object.keys(baselineData).length > 0;
    const hasBaseline2 = Object.keys(baseline2Data).length > 0;

    let data = dashboardData['month_array'].map((month) => {
      let entry = {
        name: month,
        month: month,
      };
      selectedUtilities.forEach((utility) => {
        entry[utility] = 0
        if (hasBaseline) {
          entry[utility + ' Baseline'] = 0
        }
        if (hasBaseline2) {
          entry[utility + ' Baseline 2'] = 0
        }
      })

      return entry
    })

    data.forEach((entry, index) => {
      activeIds.forEach((id) => {
        selectedUtilities.forEach((utility) => {
          let local_utility = utility.toLowerCase()
          let val = getVal(dashboardData, id, local_utility, chartType, index)
          entry[utility] += val
          if (hasBaseline) {
            let baselineVal = getVal(baselineData, id, local_utility, chartType, index)
            entry[utility + ' Baseline'] += baselineVal
          }
          if (hasBaseline2) {
            let baseline2Val = getVal(baseline2Data, id, local_utility, chartType, index)
            entry[utility + ' Baseline 2'] += baseline2Val
          }

        })
      })
    })
    return data
  }, [])

  useEffect(() => {
    setCostData(convertToChartForm(dashboardData, baselineData, baseline2Data, selectedUtilities, 'cost', activeIds))
    setUsageData(convertToChartForm(dashboardData, baselineData, baseline2Data, usageSelectedUtilities, 'usage', activeIds))
    setGHGData(convertToChartForm(dashboardData, baselineData, baseline2Data, usageSelectedUtilities, 'ghg', activeIds))


  }, [
    dashboardData, baselineData, baseline2Data, selectedUtilities, usageSelectedUtilities, activeIds, convertToChartForm
  ])


  return <>
    <Form style={{ marginTop: '25px', marginBottom: '10px' }}>
      <Form.Label style={{ display: 'inline-block', position: 'relative', 'bottom': '3px' }}>Utilities:</Form.Label>
      <Form.Row style={{ marginLeft: '0.5em', display: 'inline-block' }}>
        {availableUtilities.sort().map((utility) => (
          <Checkbox
            style={{
              fontSize: '1.25em',
              marginBottom: '0.5em',
            }}
            color={'primary-o'}
            onChange={() => {
              changeCheck(utility);
            }}
            checked={selectedUtilities.includes(utility)}
            key={utility.charAt(0).toUpperCase() +
              utility.substr(1).toLowerCase()}
          >
            <div>
              <div style={{
                fontSize: '1rem',
                position: 'relative',
                top: '-0.25em',
              }}> {utility.charAt(0).toUpperCase() +
                utility.substr(1).toLowerCase()}</div>
            </div>
          </Checkbox>
        ))}
      </Form.Row>
    </Form>
    <h5>{'Total Cost ($)'} </h5>
    <div style={{ ...row, marginBottom: '4em' }}>
      <EnergyTracerMultiLineChart
        data={!costData ? [] : costData}
        unit={'$'}
        keys={selectedUtilities}
        baseline={Object.keys(baselineData).length > 0}
        baseline2={Object.keys(baseline2Data).length > 0}
      />
      <GraphActionContainer>
        <ChartDownload
          organization_id={organization.id}
          data={costData}
          headers={chartDownloadHeaders}
          keys={chartDownloadKeys}
          chart_type={'line'}
        />
      </GraphActionContainer>
    </div>
    <h5>{'Total Usage (kBtu)'} </h5>
    <div style={{ ...row, marginBottom: '4em' }}>
      <EnergyTracerMultiLineChart
        data={!usageData ? [] : usageData}
        unit={'kbtu'}
        keys={usageSelectedUtilities}
        baseline={Object.keys(baselineData).length > 0}
        baseline2={Object.keys(baseline2Data).length > 0}
      />
      <GraphActionContainer>
        <ChartDownload
          organization_id={organization.id}
          data={usageData}
          headers={usageChartDownloadHeaders}
          keys={usageChartDownloadKeys}
          chart_type={'line'}
        />
      </GraphActionContainer>
    </div>
    {activeGHGLicense &&
      <>
        <h5>
          {'Total Emissions'}

          <Form.Group controlId={'ghg-unit-select'} style={{ display: 'inline-block', marginLeft: '1em' }}>
            <Form.Control
              as='select'
              onChange={(e) => setActiveGHGUnit(e.target.value)}
              defaultValue={activeGHGUnit}
            >
              {Object.keys(ghg_units).map((unit) => (
                <option key={`unit-opt-${unit}`} value={unit}>
                  {ghg_units[unit]}
                </option>
              ))}
            </Form.Control>
          </Form.Group>
        </h5>
        <div style={{ ...row, marginBottom: '4em' }}>
          <EnergyTracerMultiLineChart
            data={!convertedGHGData ? [] : convertedGHGData}
            unit={activeGHGUnit}
            keys={usageSelectedUtilities}
            baseline={Object.keys(baselineData).length > 0}
            baseline2={Object.keys(baseline2Data).length > 0}
          />
          <GraphActionContainer>

            <ChartDownload
              organization_id={organization.id}
              data={convertedGHGData}
              headers={usageChartDownloadHeaders}
              keys={usageChartDownloadKeys}
              chart_type={'line'}
            />
          </GraphActionContainer>
        </div></>}
  </>

}

UtilityBreakdown.propTypes = {
  baselineData: PropTypes.object,
  baseline2Data: PropTypes.object,

};

UtilityBreakdown.defaultProps = {
  baselineData: {},
  baseline2Data: {},
};

export default UtilityBreakdown;


function getVal(dataSet, id, local_utility, chartType, index) {
  let val = dataSet['building_usage'][id] &&
    dataSet['building_usage'][id][local_utility]
    ? dataSet['building_usage'][id][local_utility][chartType][
    index
    ]
    : 0
  if (chartType === 'usage') {
    val = convertUsage(val, local_utility)
  }
  return val
}

function convertUsage(val, utility) {
  if (utility === 'electric') {
    return val * 3.412142450123
  }
  if (utility === 'gas') {
    return val * 102
  }
  if (utility === 'propane') {
    return val * 91.5
  }
  if (utility === 'water') {
    return 0 //water not converted to usage
  }
  // if unsupported utility
  return val
}